import { useState } from 'react';
import { api, ApiRequestError } from '../lib/api.js';
import { Badge, Button, Drawer, Field, Input } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { money } from '../lib/format.js';

interface LineDraft { name: string; qty: string; price: string; taxable: boolean }
interface OptionDraft { name: string; recommended: boolean; lines: LineDraft[] }

const emptyLine = (): LineDraft => ({ name: '', qty: '1', price: '', taxable: true });
const emptyOption = (name: string): OptionDraft => ({ name, recommended: false, lines: [emptyLine()] });

function optionTotalCents(o: OptionDraft): number {
  return o.lines.reduce((s, l) => s + Math.round((parseFloat(l.price || '0') || 0) * 100 * (parseFloat(l.qty || '1') || 1)), 0);
}

export function NewEstimateDrawer({ customerId, onClose, onCreated }: { customerId: string; onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [taxRate, setTaxRate] = useState('0');
  const [options, setOptions] = useState<OptionDraft[]>([{ ...emptyOption('Standard'), recommended: true }]);
  const [saving, setSaving] = useState(false);

  const update = (fn: (draft: OptionDraft[]) => OptionDraft[]) => setOptions((cur) => fn(structuredClone(cur)));

  const submit = async () => {
    const payload = {
      title: title || undefined,
      taxRate: parseFloat(taxRate || '0') || 0,
      options: options.map((o) => ({
        name: o.name,
        recommended: o.recommended,
        lineItems: o.lines.filter((l) => l.name.trim()).map((l) => ({
          kind: 'service' as const,
          name: l.name,
          quantity: parseFloat(l.qty || '1') || 1,
          unitPrice: Math.round((parseFloat(l.price || '0') || 0) * 100),
          taxable: l.taxable,
        })),
      })).filter((o) => o.lineItems.length > 0),
    };
    if (payload.options.length === 0) { toast.push('Add at least one line item', 'error'); return; }
    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/estimates`, payload);
      onCreated();
    } catch (err) {
      toast.push(err instanceof ApiRequestError ? err.message : 'Could not create estimate', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer title="New estimate" onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={submit}>Create estimate</Button></>}>
      <div className="stack gap-4">
        <Field label="Title (optional)"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. AC replacement" /></Field>
        <Field label="Tax rate (%)" hint="Applied to taxable lines"><Input value={taxRate} onChange={(e) => setTaxRate(e.target.value)} inputMode="decimal" style={{ maxWidth: 120 }} /></Field>

        {options.map((o, oi) => (
          <div key={oi} className="card card-pad stack gap-3">
            <div className="row between">
              <div className="row gap-2">
                <Input value={o.name} onChange={(e) => update((d) => { d[oi]!.name = e.target.value; return d; })} style={{ maxWidth: 160, fontWeight: 600 }} />
                <label className="row gap-1" style={{ fontSize: 'var(--text-xs)' }}>
                  <input type="radio" name="recommended" checked={o.recommended} onChange={() => update((d) => d.map((x, i) => ({ ...x, recommended: i === oi })))} /> Recommended
                </label>
              </div>
              {options.length > 1 && <Button variant="ghost" size="sm" onClick={() => update((d) => d.filter((_, i) => i !== oi))}>Remove</Button>}
            </div>
            {o.lines.map((l, li) => (
              <div key={li} className="row gap-2">
                <Input placeholder="Line item" value={l.name} onChange={(e) => update((d) => { d[oi]!.lines[li]!.name = e.target.value; return d; })} className="grow" />
                <Input placeholder="Qty" value={l.qty} onChange={(e) => update((d) => { d[oi]!.lines[li]!.qty = e.target.value; return d; })} style={{ width: 64 }} inputMode="decimal" />
                <Input placeholder="Price" value={l.price} onChange={(e) => update((d) => { d[oi]!.lines[li]!.price = e.target.value; return d; })} style={{ width: 96 }} inputMode="decimal" />
                {o.lines.length > 1 && <Button variant="ghost" size="sm" onClick={() => update((d) => { d[oi]!.lines = d[oi]!.lines.filter((_, i) => i !== li); return d; })}>✕</Button>}
              </div>
            ))}
            <div className="row between">
              <Button variant="ghost" size="sm" onClick={() => update((d) => { d[oi]!.lines.push(emptyLine()); return d; })}>+ Add line</Button>
              <span className="tabular muted" style={{ fontSize: 'var(--text-sm)' }}>{money(optionTotalCents(o))}</span>
            </div>
          </div>
        ))}

        <Button variant="secondary" size="sm" onClick={() => update((d) => [...d, emptyOption(['Good', 'Better', 'Best'][d.length] ?? `Option ${d.length + 1}`)])}>
          + Add option (Good / Better / Best) <Badge tone="info">GBB</Badge>
        </Button>
      </div>
    </Drawer>
  );
}
